/**
 * Google Consent Mode v2 Utility
 *
 * This module implements Google Consent Mode v2 for compliant tracking
 * under India's DPDP Act 2023. It enables:
 *
 * - Cookieless, anonymous page view pings before consent
 * - Full tracking after user consent
 * - Granular consent control (analytics, marketing, preferences)
 *
 * @see https://developers.google.com/tag-platform/security/concepts/consent-mode
 */

// Consent mode configuration
const CONSENT_MODE_CONFIG = {
  // Default consent states (before user interaction)
  defaults: {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'denied',
    personalization_storage: 'denied',
    functionality_storage: 'granted', // Always granted for necessary cookies
    security_storage: 'granted' // Always granted for security
  },

  // Consent mode version
  version: 2,

  // Region-specific settings (India)
  region: ['IN'],

  // Wait time for consent banner (milliseconds)
  wait_for_update: 500
};

/**
 * Initialize Google Consent Mode v2
 * This must be called BEFORE GTM loads
 *
 * @param {Object} customDefaults - Optional custom default consent states
 */
export const initializeConsentMode = (customDefaults = {}) => {
  // Ensure gtag is available
  if (typeof window === 'undefined') {
    console.warn('⚠️ Consent Mode: Window not available (SSR)');
    return;
  }

  // Initialize gtag if not exists
  window.dataLayer = window.dataLayer || [];
  function gtag() {
    window.dataLayer.push(arguments);
  }
  window.gtag = window.gtag || gtag;

  // Set default consent states
  const consentDefaults = {
    ...CONSENT_MODE_CONFIG.defaults,
    ...customDefaults
  };

  window.gtag('consent', 'default', {
    ...consentDefaults,
    wait_for_update: CONSENT_MODE_CONFIG.wait_for_update,
    region: CONSENT_MODE_CONFIG.region
  });

  // Log initialization in debug mode
  if (localStorage.getItem('gtm_debug') === 'true') {
    console.log('✅ Google Consent Mode v2 initialized:', {
      defaults: consentDefaults,
      version: CONSENT_MODE_CONFIG.version
    });
  }
};

/**
 * Update consent status based on user choice
 * Called when user interacts with consent banner
 *
 * @param {Object} consentSettings - User's consent choices
 * @param {boolean} consentSettings.analytics - Analytics consent
 * @param {boolean} consentSettings.marketing - Marketing consent
 * @param {boolean} consentSettings.preferences - Preferences consent
 */
export const updateConsentMode = (consentSettings) => {
  if (typeof window === 'undefined' || !window.gtag) {
    console.warn('⚠️ Consent Mode: gtag not available');
    return;
  }

  const {
    analytics = false,
    marketing = false,
    preferences = false
  } = consentSettings;

  // Map application consent settings to Google consent types
  const googleConsentUpdate = {
    // Analytics consent controls GA4 cookies
    analytics_storage: analytics ? 'granted' : 'denied',

    // Marketing consent controls ads cookies
    ad_storage: marketing ? 'granted' : 'denied',
    ad_user_data: marketing ? 'granted' : 'denied',
    ad_personalization: marketing ? 'granted' : 'denied',

    // Preferences consent controls personalization
    personalization_storage: preferences ? 'granted' : 'denied',

    // Always granted (necessary cookies)
    functionality_storage: 'granted',
    security_storage: 'granted'
  };

  // Update consent
  window.gtag('consent', 'update', googleConsentUpdate);

  // Log update in debug mode
  if (localStorage.getItem('gtm_debug') === 'true') {
    console.log('🔄 Consent Mode updated:', {
      userChoices: consentSettings,
      googleConsent: googleConsentUpdate
    });
  }

  // Store consent timestamp for compliance
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'consent_update',
    consent_timestamp: new Date().toISOString(),
    consent_analytics: analytics,
    consent_marketing: marketing,
    consent_preferences: preferences
  });
};

/**
 * Grant all consent (Accept All button)
 */
export const grantAllConsent = () => {
  updateConsentMode({
    analytics: true,
    marketing: true,
    preferences: true
  });
};

/**
 * Deny all optional consent (Reject All button)
 * Keeps only necessary cookies
 */
export const denyAllConsent = () => {
  updateConsentMode({
    analytics: false,
    marketing: false,
    preferences: false
  });
};

/**
 * Check if consent mode is initialized
 *
 * @returns {boolean} True if consent mode is initialized
 */
export const isConsentModeInitialized = () => {
  return typeof window !== 'undefined' && typeof window.gtag === 'function';
};

/**
 * Get current consent state from dataLayer
 * Useful for debugging
 *
 * @returns {Object|null} Current consent state or null
 */
export const getCurrentConsentState = () => {
  if (typeof window === 'undefined' || !window.dataLayer) {
    return null;
  }

  // Find the most recent consent update in dataLayer
  const consentEvents = window.dataLayer.filter(
    (item) => item.event === 'consent_update'
  );

  if (consentEvents.length === 0) {
    return null;
  }

  return consentEvents[consentEvents.length - 1];
};

/**
 * Helper: Check if analytics is granted
 *
 * @returns {boolean} True if analytics consent is granted
 */
export const isAnalyticsGranted = () => {
  const state = getCurrentConsentState();
  return state ? state.consent_analytics === true : false;
};

/**
 * Helper: Check if marketing is granted
 *
 * @returns {boolean} True if marketing consent is granted
 */
export const isMarketingGranted = () => {
  const state = getCurrentConsentState();
  return state ? state.consent_marketing === true : false;
};

/**
 * Helper: Check if preferences is granted
 *
 * @returns {boolean} True if preferences consent is granted
 */
export const isPreferencesGranted = () => {
  const state = getCurrentConsentState();
  return state ? state.consent_preferences === true : false;
};

/**
 * Advanced: Set consent for specific purpose
 * Allows granular control beyond the standard three categories
 *
 * @param {Object} customConsent - Custom consent settings
 */
export const setCustomConsent = (customConsent) => {
  if (typeof window === 'undefined' || !window.gtag) {
    console.warn('⚠️ Consent Mode: gtag not available');
    return;
  }

  window.gtag('consent', 'update', customConsent);
};

// Export configuration for testing/debugging
export const getConsentModeConfig = () => CONSENT_MODE_CONFIG;

export default {
  initializeConsentMode,
  updateConsentMode,
  grantAllConsent,
  denyAllConsent,
  isConsentModeInitialized,
  getCurrentConsentState,
  isAnalyticsGranted,
  isMarketingGranted,
  isPreferencesGranted,
  setCustomConsent,
  getConsentModeConfig
};
